import React, { useCallback, useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Check, Terminal, Key, Link, Unlink } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { mcpService } from '../../services/mcpService';
import type { McpApiKey } from '../../services/mcpService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useLanguage } from '../../features/language/useLanguage';

interface MCPSettingsProps {
  readOnly?: boolean;
}

export const MCPSettings: React.FC<MCPSettingsProps> = ({ readOnly = false }) => {
  const { t } = useLanguage();
  const statusLabel = (status: string) => t(`settingsMcp.status.${status}`, undefined, status);
  const [keys, setKeys] = useState<McpApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewKey, setShowNewKey] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasActiveKey, setHasActiveKey] = useState(mcpService.hasActiveKey());
  const [manualKeyInput, setManualKeyInput] = useState('');
  const [showManualConnect, setShowManualConnect] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      const data = await mcpService.getKeys();
      setKeys(data);
    } catch (err) {
      console.error('Failed to fetch keys:', err);
      setError(t('settingsMcp.errors.loadKeys'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchKeys();
  }, [fetchKeys]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      const response = await mcpService.createKey(newKeyName);
      setKeys([response.api_key, ...keys]);
      setShowNewKey(response.key);
      setNewKeyName('');
      setIsCreating(false);
    } catch (err) {
      console.error('Failed to create key:', err);
      setError(t('settingsMcp.errors.createKey'));
    }
  };

  const handleRevokeKey = async (id: string) => {
    if (!window.confirm(t('settingsMcp.confirmRevoke'))) {
      return;
    }

    try {
      await mcpService.revokeKey(id);
      setKeys(keys.map(k => k.id === id ? { ...k, status: 'revoked' } : k));
    } catch (err) {
      console.error('Failed to revoke key:', err);
      setError(t('settingsMcp.errors.revokeKey'));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleActivateKey = (key: string) => {
    mcpService.setActiveKey(key);
    setHasActiveKey(true);
  };

  const handleDisconnect = () => {
    mcpService.clearActiveKey();
    setHasActiveKey(false);
  };

  const handleManualConnect = () => {
    if (!manualKeyInput.trim()) return;
    mcpService.setActiveKey(manualKeyInput.trim());
    setHasActiveKey(true);
    setManualKeyInput('');
    setShowManualConnect(false);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-white shadow sm:rounded-lg" data-test-id="mcp-settings-container">
      <div className="px-4 py-5 sm:p-6">
        <div className="md:flex md:items-center md:justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
              <Terminal className="h-5 w-5 text-gray-500" />
              {t('settingsMcp.title')}
            </h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>{t('settingsMcp.subtitle')}</p>
            </div>
          </div>
          {!readOnly && (
            <div className="mt-4 md:mt-0">
              <Button data-test-id="generate-new-key-button" onClick={() => setIsCreating(true)} disabled={isCreating}>
                <Plus className="h-4 w-4 mr-2" />
                {t('settingsMcp.actions.generateNewKey')}
              </Button>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200" data-test-id="mcp-error-message">
            {error}
          </div>
        )}

        {/* AI Assistant Connection Status */}
        <div className="mt-6 p-4 rounded-lg border" data-test-id="assistant-connection-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${hasActiveKey ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('settingsMcp.connection.title')}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  {hasActiveKey 
                    ? t('settingsMcp.connection.connected')
                    : t('settingsMcp.connection.disconnected')}
                </p>
              </div>
            </div>
            {!readOnly && (
              <div className="flex gap-2">
                {hasActiveKey ? (
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={handleDisconnect}
                    data-test-id="disconnect-assistant-button"
                  >
                    <Unlink className="h-4 w-4 mr-1" />
                    {t('settingsMcp.actions.disconnect')}
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    onClick={() => setShowManualConnect(!showManualConnect)}
                    data-test-id="connect-assistant-button"
                  >
                    <Link className="h-4 w-4 mr-1" />
                    {t('settingsMcp.actions.connectKey')}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Manual Key Input */}
          {showManualConnect && !hasActiveKey && (
            <div className="mt-4 pt-4 border-t border-gray-200" data-test-id="manual-connect-form">
              <p className="text-xs text-gray-500 mb-2">{t('settingsMcp.connection.manualHelp')}</p>
              <div className="flex gap-2">
                <input
                  data-test-id="manual-key-input"
                  type="password"
                  value={manualKeyInput}
                  onChange={(e) => setManualKeyInput(e.target.value)}
                  placeholder={t('settingsMcp.connection.manualPlaceholder')}
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2 border font-mono"
                />
                <Button 
                  size="sm" 
                  onClick={handleManualConnect} 
                  disabled={!manualKeyInput.trim()}
                  data-test-id="confirm-manual-connect-button"
                >
                  {t('settingsMcp.actions.connect')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Creation Form */}
        {isCreating && !showNewKey && (
          <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-200" data-test-id="create-key-form">
            <h4 className="text-sm font-medium text-gray-900 mb-2">{t('settingsMcp.createForm.title')}</h4>
            <div className="flex gap-4">
              <input
                data-test-id="new-key-name-input"
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t('settingsMcp.createForm.namePlaceholder')}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2 border"
              />
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setIsCreating(false)} data-test-id="cancel-create-key-button">{t('auth.common.cancel')}</Button>
                <Button onClick={handleCreateKey} disabled={!newKeyName.trim()} data-test-id="confirm-create-key-button">{t('settingsMcp.actions.create')}</Button>
              </div>
            </div>
          </div>
        )}

        {/* New Key Display */}
        {showNewKey && (
          <div className="mt-6 p-4 bg-green-50 rounded-md border border-green-200" data-test-id="new-key-display">
            <h4 className="text-sm font-medium text-green-800">{t('settingsMcp.newKey.title')}</h4>
            <div className="mt-2 text-sm text-green-700">
              <p>{t('settingsMcp.newKey.description')}</p>
            </div>
            <div className="mt-3">
              <div
                className="flex items-center gap-2 bg-white p-2 rounded border border-green-200 font-mono text-sm text-gray-800"
                data-test-id="new-api-key-value"
                title={t('settingsMcp.newKey.copyTooltip')}
              >
                <span className="flex-1 break-all">{showNewKey}</span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(showNewKey)}
                  className="inline-flex items-center gap-1 rounded-md border border-primary-800 bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                  data-test-id="copy-new-key-button"
                >
                  {copied === showNewKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied === showNewKey ? t('settingsMcp.actions.copied') : t('settingsMcp.actions.copyKey')}
                </button>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              {!hasActiveKey && !readOnly && (
                <Button 
                  size="sm" 
                  onClick={() => {
                    handleActivateKey(showNewKey);
                    setShowNewKey(null);
                  }}
                  data-test-id="activate-new-key-button"
                >
                  <Link className="h-4 w-4 mr-1" />
                  {t('settingsMcp.actions.connectToAssistant')}
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => setShowNewKey(null)} data-test-id="dismiss-new-key-button">{t('settingsMcp.actions.done')}</Button>
            </div>
          </div>
        )}

        {/* Keys List */}
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300" data-test-id="mcp-keys-table">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">{t('settingsMcp.table.name')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsMcp.table.keyPrefix')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsMcp.table.created')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsMcp.table.lastUsed')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsMcp.table.status')}</th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <span className="sr-only">{t('settingsMcp.table.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {keys.map((key) => (
                    <tr key={key.id} className={key.status === 'revoked' ? 'opacity-50' : ''} data-test-id={`mcp-key-row-${key.id}`}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                        <div className="flex items-center gap-2">
                          <Key className="h-4 w-4 text-gray-400" />
                          {key.name}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                        <div className="flex items-center gap-2 group">
                          <span>{key.prefix}</span>
                          <button
                            onClick={() => copyToClipboard(key.prefix)}
                            className="text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('settingsMcp.actions.copyPrefix')}
                          >
                            {copied === key.prefix ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                          </button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {new Date(key.created_at).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : t('settingsMcp.values.none')}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                          key.status === 'active' 
                            ? 'bg-green-50 text-green-700 ring-green-600/20' 
                            : 'bg-red-50 text-red-700 ring-red-600/20'
                        }`} data-test-id={`mcp-key-status-${key.id}`}>
                          {statusLabel(key.status)}
                        </span>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        {key.status === 'active' && !readOnly && (
                          <button
                            data-test-id={`revoke-key-button-${key.id}`}
                            onClick={() => handleRevokeKey(key.id)}
                            className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-auto"
                          >
                            <Trash2 className="h-4 w-4" />
                            {t('settingsMcp.actions.revoke')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
